import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import { SEOService } from '@app/core/services/seo.service';
import { TokenService } from '@app/core/services/token.service';
import * as fromRoot from '@app/reducers';
import { getPathWithoutLastFragment } from '@app/shared/helpers/location';
import { Promotion } from '@app/shared/models/promotion';
import { Team } from '@app/shared/models/team';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CreateTeamDropboxOutput } from '@smash-sdk-private/transfer/01-2024';
import { GetRecordOutput } from '@smash-sdk/domain/01-2024';
import { merge, Observable, of, Subscription } from 'rxjs';
import { delay, mapTo, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import * as fromUploader from '../../store/reducers';
import { ThemeSimplified } from '@app/shared/helpers/theme';

@Component({
  selector: 'app-dropbox-created',
  templateUrl: './dropbox-created.component.html',
  styleUrls: ['./dropbox-created.component.scss']
})
export class DropboxCreatedComponent implements OnInit, OnDestroy {

  promotion$: Observable<Promotion>;
  domain$: Observable<GetRecordOutput['record']>;
  isMainDomain$: Observable<boolean>;
  selectedTeam$: Observable<Team>;
  isPremium$: Observable<boolean>;
  dropbox$: Observable<CreateTeamDropboxOutput['dropbox']>;
  theme$: Observable<ThemeSimplified>;

  showOverlay = false;
  currentDomain: any;
  progressAnimation = of(null);
  state = 'loadingNotStarted';
  loading = false;
  replay = true;
  hasPromotion = false;
  token: string;
  showDiscoverSmash = true;
  showMyAccount = false;
  cancelClicked = false;

  subscriptions: Subscription[] = [];

  constructor(
    private store$: Store<fromRoot.State & fromUploader.State>,
    private tokenService: TokenService,
    private router: Router,
    private gtmService: GoogleTagManagerService,
    private translateService: TranslateService,
    private seoService: SEOService,
  ) {
    this.promotion$ = this.store$.select(fromUploader.getPromotionLoaded);
    this.selectedTeam$ = this.store$.select(fromRoot.getActiveTeam);
    this.domain$ = this.store$.select(fromRoot.getCurrentDomain);
    this.isMainDomain$ = this.store$.select(fromRoot.getIsMainDomain);
    this.isPremium$ = this.store$.select(fromRoot.getIsPremium);
    this.dropbox$ = this.store$.select(fromUploader.getDropbox);
    this.theme$ = this.store$.select(fromRoot.getTheme);

    this.subscriptions.push(
      this.domain$.subscribe(domain => {
        this.showOverlay = (domain.background.type === 'Youtube' || domain.background.type === 'Vimeo');
        this.currentDomain = domain;
      }),
      this.store$.select(fromRoot.getToken).subscribe((token: string) => {
        this.token = token;
      }),
    );

    // show discover Smash
    if (this.token) {
      if (!this.tokenService.isAnonymousAccount(this.token)) {// user is connected
        this.showDiscoverSmash = false;
        this.showMyAccount = true;
        if (this.currentDomain.team) {
          this.showMyAccount = true;
        }
      } else { // user is anonymous
        if (this.currentDomain.team) {
          this.showDiscoverSmash = false;
        }
      }
    }
  }

  canDeactivate(): boolean {
    return confirm(this.translateService.instant('UPLOADER_SEND_CONFIRM_NAVIGATE_AWAY'));
  }

  ngOnInit() {
    this.subscriptions.push(
      this.promotion$.pipe(withLatestFrom(this.theme$)).subscribe(([promotion, theme]) => {
        if (promotion) {
          this.hasPromotion = true;
          this.resolveLoadingState(this.progressAnimation);
          this.gtmService.pushTag({
            event: Constant.eventGTM.INTERNAL_PROMOTION_DISPLAYED,
            promotion_title: promotion.title,
            promotion_id: promotion.id,
            promotion_target: promotion.target,
            promotion_link: promotion.link,
            promotion_language: promotion.language,
          });
        } else if (theme?.reveal) {
          this.hasPromotion = true;
          this.resolveLoadingState(this.progressAnimation);
          this.gtmService.pushTag({
            event: Constant.eventGTM.DISPLAY_THEME_REVEAL,
            theme_id: theme.id,
            theme_name: theme.name,
          });
        }
      })
    );
  }

  async onCancelClicked() {
    this.cancelClicked = true;
    this.seoService.setLandingTitle();
    this.router.navigate(['/']);
  }

  async onSmashAgainClicked() {
    this.cancelClicked = true;
    this.seoService.setLandingTitle();
    this.router.navigate([getPathWithoutLastFragment()]);
  }

  private resolveLoadingState(progress: Observable<null>) {
    this.subscriptions.push(
      this.setAnimation(progress).subscribe((val: string) => {
        this.state = val;
        if (val === 'loadingFinished') {
          this.toggleTeaserMessage();
        }
      })
    );
  }

  private setAnimation(progress: Observable<null>) {
    const message = merge(
      progress.pipe(
        mapTo('loadingStarted'),
        delay(100)
      ),
      progress.pipe(
        mapTo('loadingFinished'),
        delay(2500)
      )
    );
    return message;
  }

  private toggleTeaserMessage() {
    this.replay = true;
    setTimeout(() => {
      this.replay = false;
    }, 2000);
  }

  ngOnDestroy() {
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }
}
